import { motion } from 'framer-motion'
import SEO from '../components/SEO'

const Terms = () => {
  const currentUrl = typeof window !== 'undefined' ? window.location.href : '';

  return (
    <div className="pt-32 pb-16">
      <SEO
        title="Terms of Service - Demirix IT Solutions Legal Agreement"
        description="Demirix Terms of Service. Read our terms and conditions for using our services and website. By using our services, you agree to these terms."
        keywords={['terms of service', 'terms and conditions', 'legal', 'agreement', 'Demirix terms']}
        url={currentUrl}
      />
      <div className="container-custom max-w-4xl">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="mb-12"
        >
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 dark:text-white mb-6">
            Terms of Service
          </h1>
          <p className="text-lg text-gray-600 dark:text-gray-400">
            Last updated: {new Date().toLocaleDateString('en-US', { 
              year: 'numeric', 
              month: 'long', 
              day: 'numeric' 
            })}
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="prose prose-lg max-w-none"
        >
          <div className="space-y-8">
            <section>
              <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-4">1. Acceptance of Terms</h2>
              <p className="text-gray-600 dark:text-gray-400">
                By accessing and using the Demirix website (demirix.com) and services, you accept and agree to be bound by the terms and provision of this agreement. If you do not agree to abide by the above, please do not use this service.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-4">2. Description of Service</h2>
              <p className="text-gray-600 dark:text-gray-400 mb-4">
                Demirix provides IT consulting, web development, cloud solutions, and related technology services. Our services include but are not limited to:
              </p>
              <ul className="list-disc pl-6 text-gray-600 dark:text-gray-400">
                <li>Web development and design</li>
                <li>Cloud infrastructure and migration services</li>
                <li>IT consulting and strategy</li>
                <li>Mobile application development</li>
                <li>E-commerce solutions</li>
                <li>Technical support and maintenance</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-4">3. Use License</h2>
              <p className="text-gray-600 dark:text-gray-400 mb-4">
                Permission is granted to temporarily download one copy of the materials on Demirix's website for personal, non-commercial transitory viewing only. This is the grant of a license, not a transfer of title, and under this license you may not:
              </p>
              <ul className="list-disc pl-6 text-gray-600 dark:text-gray-400">
                <li>modify or copy the materials</li>
                <li>use the materials for any commercial purpose or for any public display</li>
                <li>attempt to reverse engineer any software contained on the website</li>
                <li>remove any copyright or other proprietary notations from the materials</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-4">4. Service Terms</h2>
              <h3 className="text-xl font-medium text-gray-900 dark:text-white mb-3">Project Scope and Deliverables</h3>
              <p className="text-gray-600 dark:text-gray-400 mb-4">
                All projects will be clearly defined in a separate Statement of Work (SOW) or project agreement. The scope, timeline, deliverables, and payment terms will be specified in writing before work begins.
              </p>
              
              <h3 className="text-xl font-medium text-gray-900 dark:text-white mb-3">Payment Terms</h3>
              <p className="text-gray-600 dark:text-gray-400 mb-4">
                Payment terms will be specified in individual project agreements. Generally:
              </p>
              <ul className="list-disc pl-6 text-gray-600 dark:text-gray-400">
                <li>A deposit may be required before work begins</li>
                <li>Invoices are due within 30 days of issuance</li>
                <li>Late payments may incur additional fees</li>
                <li>Work may be suspended for overdue accounts</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-4">5. Intellectual Property</h2>
              <p className="text-gray-600 dark:text-gray-400 mb-4">
                Upon full payment for services:
              </p>
              <ul className="list-disc pl-6 text-gray-600 dark:text-gray-400 mb-4">
                <li>Clients receive ownership of custom work created specifically for them</li>
                <li>Demirix retains rights to general methodologies, techniques, and know-how</li>
                <li>Third-party components remain under their respective licenses</li>
                <li>Demirix may showcase completed work in portfolios unless otherwise agreed</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-4">6. Client Responsibilities</h2>
              <p className="text-gray-600 dark:text-gray-400 mb-4">
                Clients are responsible for:
              </p>
              <ul className="list-disc pl-6 text-gray-600 dark:text-gray-400">
                <li>Providing accurate and complete project requirements</li>
                <li>Timely provision of necessary materials, access, and feedback</li>
                <li>Ensuring they have rights to any materials provided to us</li>
                <li>Maintaining backups of their data</li>
                <li>Complying with applicable laws and regulations</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-4">7. Warranties and Disclaimers</h2>
              <p className="text-gray-600 dark:text-gray-400 mb-4">
                We warrant that our services will be performed in a professional manner. However:
              </p>
              <ul className="list-disc pl-6 text-gray-600 dark:text-gray-400">
                <li>We cannot guarantee specific business results</li>
                <li>We disclaim all other warranties, express or implied</li>
                <li>We are not liable for third-party service failures</li>
                <li>Bug fixes and minor revisions are included for 30 days post-delivery</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-4">8. Limitation of Liability</h2>
              <p className="text-gray-600 dark:text-gray-400">
                In no event shall Demirix or its suppliers be liable for any damages (including, without limitation, damages for loss of data or profit, or due to business interruption) arising out of the use or inability to use the materials on Demirix's website or services, even if Demirix or its authorized representative has been notified orally or in writing of the possibility of such damage. Our total liability shall not exceed the amount paid for the specific service giving rise to the claim.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-4">9. Privacy and Confidentiality</h2>
              <p className="text-gray-600 dark:text-gray-400">
                We respect your privacy and maintain strict confidentiality regarding your business information. Our privacy practices are detailed in our Privacy Policy. We may require mutual non-disclosure agreements for sensitive projects.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-4">10. Termination</h2>
              <p className="text-gray-600 dark:text-gray-400 mb-4">
                Either party may terminate services with written notice:
              </p>
              <ul className="list-disc pl-6 text-gray-600 dark:text-gray-400">
                <li>Immediate termination for material breach</li>
                <li>30-day notice for termination without cause</li>
                <li>Client remains liable for work completed and expenses incurred</li>
                <li>Deliverables will be provided upon full payment</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-4">11. Governing Law</h2>
              <p className="text-gray-600 dark:text-gray-400">
                These terms are governed by the laws of the United Arab Emirates. Any disputes will be resolved through binding arbitration in Dubai, UAE, or through the competent courts of Dubai.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-4">12. Force Majeure</h2>
              <p className="text-gray-600 dark:text-gray-400">
                Neither party shall be liable for any failure or delay in performance due to circumstances beyond their reasonable control, including but not limited to acts of God, war, terrorism, or government restrictions.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-4">13. Modifications</h2>
              <p className="text-gray-600 dark:text-gray-400">
                Demirix may revise these terms at any time without notice. By using this website and our services, you are agreeing to be bound by the then current version of these terms.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-4">14. Contact Information</h2>
              <p className="text-gray-600 dark:text-gray-400 mb-4">
                If you have any questions about these Terms of Service, please contact us:
              </p>
              <div className="bg-gray-50 dark:bg-gray-800 p-6 rounded-lg">
                <p className="text-gray-600 dark:text-gray-400 mb-2"><strong>Email:</strong> info@demirix.com</p>
                <p className="text-gray-600 dark:text-gray-400 mb-2"><strong>Address:</strong> Dubai, United Arab Emirates</p>
                <p className="text-gray-600 dark:text-gray-400"><strong>Website:</strong> demirix.com</p>
              </div>
            </section>
          </div>
        </motion.div>
      </div>
    </div>
  )
}

export default Terms